import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import MediaManagement from './MediaManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Section = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h2`
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 600;
`;

const Form = styled.form`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.75rem;
  color: white;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TextArea = styled.textarea`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.75rem;
  color: white;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.75rem;
  color: white;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
  
  option {
    background: #2d3748;
    color: white;
  }
`;

const Button = styled.button`
  background: #667eea;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
  
  &.danger {
    background: #f56565;
    
    &:hover {
      background: #e53e3e;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ImageCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    border-color: #667eea;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
`;

const ImageContent = styled.div`
  padding: 1rem;
`;

const ImageTitle = styled.h3`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ImageDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const ImageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  margin-bottom: 1rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.active {
    background: rgba(72, 187, 120, 0.2);
    color: #48bb78;
  }
  
  &.inactive {
    background: rgba(245, 101, 101, 0.2);
    color: #f56565;
  }
`;

const ErrorMessage = styled.div`
  color: #f56565;
  background: rgba(245, 101, 101, 0.1);
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid rgba(245, 101, 101, 0.3);
`;

const SuccessMessage = styled.div`
  color: #48bb78;
  background: rgba(72, 187, 120, 0.1);
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid rgba(72, 187, 120, 0.3);
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #667eea;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const categories = [
  'Petrochemical',
  'Polymer',
  'Research',
  'Industrial',
  'Manufacturing',
  'Laboratory',
  'Technology',
  'Other'
];

function IndustryImageManagement() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingImage, setEditingImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    image_alt_text: '',
    category: 'Petrochemical',
    display_order: 0,
    is_active: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/industry-images`, {
        headers: getAuthHeaders()
      });
      
      const result = await response.json();
      
      if (result.success) {
        setImages(result.data);
      } else {
        setError('Failed to fetch industry images');
      }
    } catch (err) {
      setError('Error fetching industry images: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingImage 
        ? `${API_BASE_URL}/api/v1/industry-images/${editingImage.id}`
        : `${API_BASE_URL}/api/v1/industry-images`;
      
      const method = editingImage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(editingImage ? 'Image updated successfully!' : 'Image created successfully!');
        resetForm();
        fetchImages();
      } else {
        setError(result.message || 'Failed to save image');
      }
    } catch (err) {
      setError('Error saving image: ' + err.message);
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title,
      description: image.description,
      image_url: image.image_url,
      image_alt_text: image.image_alt_text,
      category: image.category,
      display_order: image.display_order,
      is_active: image.is_active
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/industry-images/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Image deleted successfully!');
        fetchImages();
      } else {
        setError(result.message || 'Failed to delete image');
      }
    } catch (err) {
      setError('Error deleting image: ' + err.message);
    }
  };

  const resetForm = () => {
    setEditingImage(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      image_alt_text: '',
      category: 'Petrochemical',
      display_order: 0,
      is_active: true
    });
    setSelectedImage(null);
  };

  const handleImageSelect = (mediaFile) => {
    setSelectedImage(mediaFile);
    setFormData(prev => ({
      ...prev,
      image_url: mediaFile.url,
      image_alt_text: mediaFile.fileName
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <Section>
        <Title>Industry Images</Title>
        <LoadingSpinner />
      </Section>
    );
  }

  return (
    <Section>
      <Title>Industry Images Management</Title>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Title *</Label>
          <Input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Enter image title"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter image description"
          />
        </FormGroup>

        <FormGroup>
          <Label>Category</Label>
          <Select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Display Order</Label>
          <Input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleInputChange}
            min="0"
          />
        </FormGroup>

        <FormGroup>
          <Label>Image URL</Label>
          <Input
            type="url"
            name="image_url"
            value={formData.image_url}
            onChange={handleInputChange}
            placeholder="Enter image URL or select from media library"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Alt Text</Label>
          <Input
            type="text"
            name="image_alt_text"
            value={formData.image_alt_text}
            onChange={handleInputChange}
            placeholder="Enter alt text for accessibility"
          />
        </FormGroup>

        <FormGroup>
          <Label>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              style={{ marginRight: '0.5rem' }}
            />
            Active
          </Label>
        </FormGroup>

        <ButtonGroup>
          <Button type="submit" disabled={!formData.title || !formData.image_url}>
            {editingImage ? 'Update Image' : 'Create Image'}
          </Button>
          {editingImage && (
            <Button type="button" className="secondary" onClick={resetForm}>
              Cancel
            </Button>
          )}
        </ButtonGroup>
      </Form>

      <MediaManagement onImageSelect={handleImageSelect} selectedImage={selectedImage} />

      <ImageGrid>
        {images.map((image) => (
          <ImageCard key={image.id}>
            <ImagePreview src={image.image_url} alt={image.image_alt_text} />
            <ImageContent>
              <ImageTitle>{image.title}</ImageTitle>
              <ImageDescription>{image.description}</ImageDescription>
              <ImageMeta>
                <span>{image.category}</span>
                <StatusBadge className={image.is_active ? 'active' : 'inactive'}>
                  {image.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </ImageMeta>
              <ButtonGroup>
                <Button className="secondary" onClick={() => handleEdit(image)}>
                  Edit
                </Button>
                <Button className="danger" onClick={() => handleDelete(image.id)}>
                  Delete
                </Button>
              </ButtonGroup>
            </ImageContent>
          </ImageCard>
        ))}
      </ImageGrid>
    </Section>
  );
}

export default IndustryImageManagement;
